import Papa from 'papaparse';
import { supabase } from '../../../lib/supabase';
import { fetchAll } from '../../../lib/save';
import { decodeCatalogBytes, stripToHeader } from '../../../lib/catalogParse';
import { nextReference, withAccountEdge, waitingState } from './orderJobs';

/*
  DOOR 4, A CLAIMSOFT TRANSFER ORDER COMING IN. Ordering stage 1, 11 October 2026.

  **What it is for**, docs/orders/DOOR-4-THE-CLAIMSOFT-RUN.md: a regional office
  runs short, ClaimSoft writes a transfer order saying what it needs, and he
  pulls it as a file. This turns that file into a job in the queue, one job per
  transfer order number, so picking a job up IS the run, his words: *"this part
  is really replaced by me picking up an order to process from my job queue."*

  **Every line keeps every column exactly as ClaimSoft sent it** (`source_row`),
  because the two files that go back into ClaimSoft at the end are this same
  transfer order carried through whole, his correction of 4 October: *"all the
  data that was on the file at the beginning would still need to be on the
  sheet with the exception of deletion or qty changes."* And each line keeps its
  transfer order number, which is what ClaimSoft matches the import back on.

  **Each line is looked up in the item record**: its ClaimSoft number (main or
  Medicare) leads to the catalog item and its vendor, matched on 11 October. A
  line OnHand cannot find makes the job **Needs you**, never a guess.

  **Nothing about routing is decided here.** Buy, move from Austin, or skip is
  stage 4.
*/

/* ="00002" keeps a leading zero in Excel. The number is 00002. */
export const unwrap = v => { const s = String(v ?? '').trim(); const m = s.match(/^="(.*)"$/); return (m ? m[1] : s).trim(); };

/*
  **The file, split into transfer orders.** Pure, so it can be tested: no
  database. Lines with no item number are dropped and counted. A line whose
  quantity is not above zero is kept OUT of the job and counted, because a job
  line must order something, and the count is said on screen rather than lost.
*/
export function parseTransferOrders(text) {
  const parsed = Papa.parse(stripToHeader(text), { header: true, skipEmptyLines: 'greedy' });
  const fields = parsed.meta.fields || [];
  const missing = ['TONumber', 'ItemNumber', 'QuantityOrdered', 'DestinationWarehouse'].filter(f => !fields.includes(f));
  if (missing.length) {
    return { orders: [], problem: 'This does not look like a ClaimSoft transfer order: it has no ' + missing.join(', ') + ' column.' };
  }
  const by = new Map();
  let noItem = 0, noQuantity = 0;
  for (const row of parsed.data) {
    const item = unwrap(row.ItemNumber);
    if (!item) { noItem++; continue; }
    const quantity = Number(String(row.QuantityOrdered ?? '').replace(/,/g, ''));
    if (!(quantity > 0)) { noQuantity++; continue; }
    const to = unwrap(row.TONumber);
    if (!by.has(to)) by.set(to, { toNumber: to, destination: String(row.DestinationWarehouse || '').trim(),
      source: String(row.SourceWarehouse || '').trim(), lines: [] });
    by.get(to).lines.push({ item, description: String(row.Description || '').trim(), vendor: String(row.Vendor || '').trim(),
      quantity, row });
  }
  return { orders: [...by.values()], noItem, noQuantity, problem: by.size ? null : 'There are no lines with an item and a quantity in this file.' };
}

export async function readTransferOrderFile(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  return parseTransferOrders(decodeCatalogBytes(bytes));
}

/* ClaimSoft number -> the company's item and its catalog vendor. Main and
   Medicare numbers both count: a Medicare number is the same item. */
export async function lookUpClaimSoft(numbers) {
  const keys = [...new Set(numbers.map(n => String(n).toUpperCase()))];
  const out = new Map();
  for (let i = 0; i < keys.length; i += 150) {
    const { data, error } = await supabase.from('org_item_numbers')
      /* org_items points at the catalog twice (the item, and what replaces it),
         so the join names which. Without it the request is refused outright. */
      .select('number_key, org_item:org_items(catalog_item_id, item:catalog_items!org_items_catalog_item_id_fkey(vendor_id))')
      .eq('system', 'claimsoft').in('number_key', keys.slice(i, i + 150));
    if (error) return { found: out, error };
    for (const r of data || []) {
      if (r.org_item) out.set(r.number_key, { catalog_item_id: r.org_item.catalog_item_id, vendor_id: r.org_item.item ? r.org_item.item.vendor_id : null });
    }
  }
  return { found: out, error: null };
}

/* ClaimSoft's warehouse name -> one of the 952 places. Upper and lower case are
   the same; anything else must match exactly, or the job waits for him. */
async function findDestination(name) {
  if (!name) return null;
  const { data } = await supabase.from('warehouses').select('id, name').ilike('name', name.replace(/[%_\\]/g, '\\$&'));
  const exact = (data || []).filter(w => w.name.trim().toUpperCase() === name.trim().toUpperCase());
  return exact.length === 1 ? exact[0].id : null;
}

/*
  **Bring the transfer orders in.** One job each. A transfer order already in
  the queue (and not cancelled) is refused rather than doubled, because pulling
  the same file twice is easy and two jobs for one order would be ordered twice.

  Each job is written, then its lines, then the lines are counted back; if the
  count is short the job is removed again, so half an order never sits in the
  queue looking whole. The same pattern as placing an order (door 5).
*/
export async function importTransferOrders(orders) {
  const results = [];
  const lookup = await lookUpClaimSoft(orders.flatMap(o => o.lines.map(l => l.item)));
  if (lookup.error) return { results, error: lookup.error };

  for (const o of orders) {
    const ref = 'TO ' + o.toNumber;
    const { data: already, error: aErr } = await supabase.from('order_jobs')
      .select('reference, state').eq('door', 'transfer_order').eq('source_reference', ref).neq('state', 'cancelled');
    if (aErr) { results.push({ toNumber: o.toNumber, ok: false, why: aErr.message }); continue; }
    if (already && already.length) { results.push({ toNumber: o.toNumber, ok: false, why: 'already in the queue as ' + already[0].reference }); continue; }

    const destinationId = await findDestination(o.destination);
    const unknown = o.lines.filter(l => !lookup.found.has(l.item.toUpperCase())).length;
    /* Ready only with every line known AND carrying an Account Edge number,
       his rule of 11 October 2026. */
    const known = o.lines.map(l => ({ catalog_item_id: (lookup.found.get(l.item.toUpperCase()) || {}).catalog_item_id || null }));
    const { lines: withAe } = await withAccountEdge(known);
    const noAe = withAe.filter(l => l.catalog_item_id && !l.ae_number).length;
    const startState = waitingState({ destination_id: destinationId }, withAe);
    const { reference, error: refError } = await nextReference();
    if (refError) { results.push({ toNumber: o.toNumber, ok: false, why: refError.message }); continue; }

    const { data: job, error } = await supabase.from('order_jobs').insert({
      reference, door: 'transfer_order', state: startState,
      destination_id: destinationId, source_reference: ref, requested_by_name: 'ClaimSoft',
      note: destinationId ? null : 'ClaimSoft sent this to "' + o.destination + '", which is not one of the places OnHand knows.',
    }).select('id, reference').maybeSingle();
    if (error || !job) { results.push({ toNumber: o.toNumber, ok: false, why: error ? error.message : 'the job did not save' }); continue; }

    const lines = o.lines.map((l, i) => {
      const hit = lookup.found.get(l.item.toUpperCase());
      return { job_id: job.id, line_no: i + 1, item_as_given: l.item, description: l.description || null,
        quantity: l.quantity, catalog_item_id: hit ? hit.catalog_item_id : null, vendor_id: hit ? hit.vendor_id : null,
        claimsoft_to_number: o.toNumber, source_row: l.row };
    });
    const { error: lineError } = await supabase.from('order_job_lines').insert(lines);
    const { count } = lineError ? { count: 0 } : await supabase.from('order_job_lines')
      .select('id', { count: 'exact', head: true }).eq('job_id', job.id);
    if (lineError || count !== lines.length) {
      await supabase.from('order_jobs').delete().eq('id', job.id);
      results.push({ toNumber: o.toNumber, ok: false, why: lineError ? lineError.message : 'only ' + count + ' of ' + lines.length + ' lines saved, so nothing was kept' });
      continue;
    }
    results.push({ toNumber: o.toNumber, ok: true, reference: job.reference, lines: lines.length, unknown, noAe, destinationKnown: !!destinationId });
  }
  return { results, error: null };
}

/*
  **What a matched line needs for ordering**: its main Account Edge number and
  its current price, for the job window. Read for the items on one job only.
*/
export async function lineDetails(catalogItemIds) {
  const ids = [...new Set(catalogItemIds.filter(Boolean))];
  const ae = new Map(), price = new Map();
  if (!ids.length) return { ae, price, error: null };
  const { data: items, error } = await fetchAll(() => supabase.from('org_items')
    .select('catalog_item_id, numbers:org_item_numbers(system, number, is_main)').in('catalog_item_id', ids));
  if (error) return { ae, price, error };
  for (const it of items || []) {
    const main = (it.numbers || []).find(n => n.system === 'account_edge' && n.is_main);
    if (main) ae.set(it.catalog_item_id, main.number);
  }
  const { data: prices, error: pErr } = await fetchAll(() => supabase.from('catalog_current_prices')
    .select('catalog_item_id, cost_per_uom, purchase_uom, uom_key, book_label, priced_on').in('catalog_item_id', ids));
  if (pErr) return { ae, price, error: pErr };
  for (const p of prices || []) {
    const had = price.get(p.catalog_item_id);
    if (!had || (p.uom_key === 'EA' && had.uom_key !== 'EA')) price.set(p.catalog_item_id, p);
  }
  return { ae, price, error: null };
}
